import { useCallback, useEffect, useRef } from 'react';
import useApi from '../../api/backendApiContext';
import { useMutation } from '@tanstack/react-query';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { errorToString } from '../../util/utils';
import { useNotification } from '../../notification/notificationsContext';
import { useQueryCacheManager } from '../../hooks/useQueryCacheManager';
import { useSearchParams } from 'react-router-dom';
import { instagramConfig } from '../../commons/config';
import { openUrlInNewTab } from '../../util/window-utils';

export const useConnectInstagram = () => {
  const { socialPostingApi } = useApi();
  const { notifySuccess, notifyError } = useNotification();
  const queryCacheManager = useQueryCacheManager();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isLoading: isConnecting, mutate: connect } = useMutation(
    async (code: string) =>
      socialPostingApi.authenticateInstagram(code, instagramConfig.redirectUrl),
    {
      onSuccess: (instagramAccount) => {
        queryCacheManager.setInstagramAccount(instagramAccount);
        const message = 'Instagram account has been successfully connected.';
        notifySuccess({
          notificationMsg: message,
          logMsg: message,
        });
        analyticsTrack(AnalyticsEvent.INSTAGRAM_CONNECTED);
      },
      onError: (e) => {
        analyticsTrack(AnalyticsEvent.INSTAGRAM_CONNECTION_FAILED);
        const message = errorToString(
          e,
          'Something went wrong while setting up Instagram connection. Please try again.'
        );
        notifyError({
          notificationMsg: message,
          logMsg: message,
        });
      },
    }
  );

  const previousCode = useRef<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    if (code && code !== previousCode.current && !isConnecting) {
      previousCode.current = code;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('code');
      setSearchParams(newSearchParams, { replace: true });

      connect(code);
    }
  }, [searchParams, connect, isConnecting, setSearchParams]);

  const startConnect = useCallback(() => {
    try {
      openUrlInNewTab(instagramConfig.embedUrl);
      logger.info('Initiating Instagram login.');
    } catch (error) {
      const notificationMsg = 'Connection with Instagram was not complete, please try again.';
      notifyError({
        err: error,
        notificationMsg: notificationMsg,
      });
    }
  }, [notifyError]);

  return { startConnect, isConnecting };
};
